import React from 'react';
import { Tabs } from 'antd';
import {
  AndroidOutlined,
  AppleOutlined,
  UserOutlined,
  WindowsOutlined,
  DeliveredProcedureOutlined,
  DiffOutlined,
  CarOutlined,
  SafetyCertificateOutlined,
  HistoryOutlined,
  DollarOutlined,
  LinkOutlined,
  KeyOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';

import TabDrivers from './CompanyTabs/TabDrivers';
import UserCompanies from '../../../UserCompanies/UserCompanies';
import Subscription from './CompanyTabs/Subscription';
import MainField from './CompanyTabs/MainField';
import Stripe from './CompanyTabs/Stripe';
import IFTA from './CompanyTabs/IFTA/IFTA';
import VehiclesCompany from '../../Vehicles/VehiclesCompany';
import FreeTrial from './CompanyTabs/FreeTrial';
import SubcriptionQuantity from './CompanyTabs/SubcriptionQuantity';
import SubscriptionUpdate from './CompanyTabs/SUBSCRIPTION_History';
import ContractTab from './CompanyTabs/Contract/Contract';
import NotificationCompany from './CompanyTabs/Notfications/NotificationCompany';

import Integration from './Integration';
import ApiKeys from './CompanyTabs/ApiKeys';
import LogEditAccess from './CompanyTabs/LogEditAccess';

import { useCompanyData } from '../../../../../../Hooks/TT_ELD/Companies';
import useRouterQueryTabs from '../../../../../../Utils/useRouterQueryTabs';
import { useTagQuerySync } from '../../../../../../Utils/useTagQuerySync';
import {
  isWhiteLabelAdmin,
  WHITE_LABEL_HIDDEN_COMPANY_TABS,
} from '../../../../../../Utils/whiteLabel';
import { hasRteAccess, useProjectData } from '../../../../../../Hooks/useProjectData';

const getUserFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

/* ================= component ================= */

export const Company: React.FC = () => {
  const { id } = useParams();
  const user = getUserFromStorage();
  const userRole: string = user?.role || '';
  const userConfig = user?.config || {};
  const isWhiteLabel = isWhiteLabelAdmin();
  const { projectData } = useProjectData();
  const isRte = hasRteAccess(projectData?.projectName);
  const hiddenCompanyTabs = isRte
    ? WHITE_LABEL_HIDDEN_COMPANY_TABS.filter((key) => key !== '11')
    : WHITE_LABEL_HIDDEN_COMPANY_TABS;

  const canSupportSeeStripeTabs =
    userRole !== 'support' ||
    (userConfig['read_companies-stripe-management'] === true &&
      userConfig['write_companies-stripe-management'] === false);

  const queryKey = `tabKey_user_${id}`;
  const [tabKey, setTabKey] = useRouterQueryTabs('1', queryKey, String);

  useTagQuerySync(
    { en_US: `Company #${id}`, zh_CN: '' },
    `user/company/${id}`,
    { [queryKey]: tabKey },
    { [queryKey]: setTabKey },
  );

  const { data }: any = useCompanyData(id);

  const items = [
    {
      key: '1',
      label: 'Main fields',
      children: <MainField />,
      icon: <AppleOutlined rev={undefined} />,
      roles: ['developer', 'manager', 'sales', 'support', 'support_manager'],
    },
    {
      key: '2',
      label: 'Stripe',
      children: <Stripe />,
      icon: <AndroidOutlined />,
      roles: ['developer', 'manager', 'sales'],
    },

    ...(canSupportSeeStripeTabs
      ? [
          {
            key: '3',
            label: 'Subscription',
            children: <Subscription />,
            icon: <DeliveredProcedureOutlined rev={undefined} />,
            roles: ['developer', 'manager', 'sales', 'support', 'support_manager'],
          },
          {
            key: '4',
            label: 'Subscription history',
            children: <SubscriptionUpdate />,
            icon: <HistoryOutlined rev={undefined} />,
            roles: ['developer', 'manager', 'sales', 'support', 'support_manager'],
          },
        ]
      : []),

    {
      key: '5',
      label: 'Subscription update',
      children: <SubcriptionQuantity />,
      icon: <DeliveredProcedureOutlined rev={undefined} />,
      roles: ['developer', 'manager', 'sales', 'support_manager'],
    },
    {
      key: '6',
      label: 'Contract',
      children: <ContractTab />,
      icon: <DollarOutlined />,
      roles: ['developer', 'manager', 'sales'],
    },
    {
      key: '7',
      label: 'Drivers',
      children: <TabDrivers />,
      icon: <WindowsOutlined rev={undefined} />,
      roles: ['developer', 'manager', 'sales', 'support', 'support_manager'],
    },
    {
      key: '8',
      label: 'Users',
      children: <UserCompanies />,
      icon: <UserOutlined rev={undefined} />,
      roles: ['developer', 'manager', 'sales', 'support', 'support_manager'],
    },
    {
      key: '9',
      label: 'Vehicles',
      children: <VehiclesCompany />,
      icon: <DiffOutlined rev={undefined} />,
      roles: ['developer', 'manager', 'sales', 'support', 'support_manager'],
    },
    {
      key: '10',
      label: 'IFTA',
      children: <IFTA />,
      icon: <SafetyCertificateOutlined rev={undefined} />,
      roles: ['developer', 'manager', 'support', 'support_manager'],
    },

    ...(data?.stripeStatus !== 'active'
      ? [
          {
            key: '11',
            label: 'Free trial',
            children: <FreeTrial />,
            icon: <CarOutlined rev={undefined} />,
            roles: ['developer', 'manager', 'sales', 'support', 'support_manager'],
          },
        ]
      : []),

    {
      key: '12',
      label: 'Notification',
      children: <NotificationCompany />,
      icon: <SafetyCertificateOutlined rev={undefined} />,
      roles: ['developer', 'manager', 'support', 'support_manager'],
    },
    {
      key: '13',
      label: 'Integration',
      children: <Integration />,
      icon: <LinkOutlined rev={undefined} />,
      roles: ['developer', 'manager', 'support', 'support_manager'],
    },
    {
      label: <span>API Keys</span>,
      key: '14',
      children: <ApiKeys />,
      icon: <KeyOutlined rev={undefined} />,
      roles: ['developer', 'manager', 'support', 'support_manager'],
    },

    ...(data?.stripeStatus !== 'active'
      ? [
          {
            key: '15',
            label: 'Log Edit Access',
            children: <LogEditAccess />,
            icon: <FieldTimeOutlined rev={undefined} />,
            roles: ['sales', 'manager'],
          },
        ]
      : []),
  ];

  const filteredItems = items.filter((tab) => {
    // role
    if (!tab.roles?.includes(userRole)) return false;

    // white label
    if (isWhiteLabel && hiddenCompanyTabs.includes(tab.key)) return false;

    // support stripe special
    if (userRole === 'support' && (tab.key === '3' || tab.key === '4')) {
      return canSupportSeeStripeTabs;
    }

    return true;
  });

  // agar URL orqali yopiq tab kelib qolsa → 1-tabga tushiramiz
  const safeActiveKey = filteredItems.some((t) => t.key === tabKey)
    ? tabKey
    : filteredItems[0]?.key;

  return (
    <div className="mainPage">
      <Tabs
        activeKey={safeActiveKey}
        onChange={setTabKey}
        items={filteredItems}
        style={{ height: '100%' }}
      />
    </div>
  );
};

export default Company;
